<template>
  <div>
    <!-- Page Header Start -->
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          :parentRouteName="isBookingCenter ? '' : 'BookingSelectList'" 
          :extra-items="isBookingCenter ? [
            '登記預約中心',
            {
              text: bookingPreset.name ?? '',
              to: 'BookingCenterBookingList',
              params: $route.params.id
            },
            isReadOnly ? '檢視預約' : isEditing ? '編輯預約' : '新增預約'
          ] : [
            {
              text: bookingPreset.name ?? '',
              to: 'BookingList',
              params: $route.params.id
            },
            isReadOnly ? '檢視預約' : isEditing ? '編輯預約' : '新增預約'
          ]" 
        />
      </div>
    </div>
    <!-- Page Header End -->

    <!-- Form Start -->
    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-12">
          <b-overlay :show="showLoading">
            <b-card>
              <!-- begin: dynamic form -->
              <div class="preserve-spacing-for-datetime-picker">
                <h4 class="mb-2 font-weight-bold">
                  {{ isReadOnly ? "檢視預約" : (isEditing ? "編輯預約" : "新增預約") }} -
                  {{ bookingPreset.name }}
                </h4>

                <b-form-group
                  label="預約號碼"
                  label-cols-sm="3"
                  label-cols-lg="2"
                  content-cols-lg="9"
                >
                  <label class="col-form-label" style="font-size: 1rem">{{ booking.booking_number }}</label>
                </b-form-group>

                <b-form-group
                  v-bind:label="'預約會員'"
                  label-cols-sm="3"
                  label-cols-lg="2"
                  content-cols-lg="9"
                  style="width: 100%"
                >
                  <div
                    v-if="customer"
                    class="d-flex align-items-center py-2 staff-info"
                  >
                    <b-avatar
                      class="mx-2"
                      :src="customer.avatar_url"
                    ></b-avatar>
                    <div
                      class="mr-auto font-weight-bold text-nowrap text-truncate"
                    >
                      {{ customer.name }}
                      <span class="staff-list-info">
                        {{ customer.email }}<br />
                        {{ displayCustomerInfo(customer) }}
                      </span>
                    </div>
                  </div>
                  <StaffAddCustomer
                    v-if="!isReadOnly"
                    ref="staffAddCustomer"
                    :add-button-text="addCustomerButtonText"
                    @bind="handleSelectCustomer"
                    :sourceType="'line'"
                  ></StaffAddCustomer>
                </b-form-group>

                <!-- wizard 模式 start-->
                <div class="sorted-fields">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="分店"
                    label-for="staff"
                    v-if="bookingPreset.type === 'wizard' && dataModel.branchSelectionIsEnabled"
                    :style="{ order: fieldOrder.branch }"
                  >
                    <AppSingleSelect
                      class="mr-3"
                      v-model="booking.select_branch"
                      :options="branchOptions"
                      @input="handleBranchChange"
                      :disabled="isReadOnly || disabledBranchSelect"
                    />
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="員工"
                    label-for="staff"
                    v-if="bookingPreset.type === 'wizard' && dataModel.staffSelectionIsEnabled"
                    :style="{ order: fieldOrder.staff }"
                  >
                    <div class="d-flex">
                      <b-form-text
                        v-if="booking.staff"
                        class="mb-2 mr-sm-2 mb-sm-0"
                      >
                        {{ booking.staff.name }}
                      </b-form-text>
                      <b-button
                        v-if="!isReadOnly"
                        class="mb-xl-0"
                        variant="primary"
                        @click="$refs.staffSelection.show()"
                        :disabled="!booking.branch_id"
                      >
                        <b-spinner v-if="isFetchingStaffs" small></b-spinner>
                        <i class="fa fa-plus" v-else></i>
                        選擇員工
                      </b-button>
                    </div>
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="服務"
                    label-for="service"
                    v-if="bookingPreset.type === 'wizard' && dataModel.serviceFieldId"
                    :style="{ order: fieldOrder.service }"
                  >
                    <b-overlay :show="isLoadingServices">
                      <div v-if="canMultipleSelect">
                        <b-form-checkbox-group
                          v-model="booking.service_id"
                          :options="serviceOptions"
                          :disabled="isReadOnly"
                          @input="handleServiceSelected"
                        >
                        </b-form-checkbox-group>
                      </div>
                      <div v-else>
                        <b-form-radio-group
                          v-model="booking.service_id"
                          :options="serviceOptions"
                          :disabled="isReadOnly"
                          @input="handleServiceSelected"
                        >
                        </b-form-radio-group>
                      </div>
                    </b-overlay>
                  </b-form-group>
                </div>
                <!-- wizard 模式 end-->

                <DynamicFormDashboard
                  ref="dynamicForm"
                  :input-subjects="visibleSubjects"
                  v-model="inputForm"
                ></DynamicFormDashboard>

                <!-- 補充時間欄位 start-->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="開始時間"
                  label-for="start_at"
                  v-if="false === hasSetUpFieldMappingForStartAt"
                >
                  <AppDateTimePicker
                    id="start_at"
                    v-model="start_at"
                    :hasError="deepGet(v$, 'start_at.$error')"
                    :disabled="isReadOnly"
                  ></AppDateTimePicker>
                  <b-form-invalid-feedback
                    :state="!deepGet(v$, 'start_at.$error')"
                  >
                    輸入的資料有誤或未填寫，請確認
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="結束時間"
                  label-for="end_at"
                  v-if="shouldShowDefaultEndAtField"
                >
                  <AppDateTimePicker
                    id="end_at"
                    v-model="end_at"
                    :hasError="deepGet(v$, 'end_at.$error')"
                    :disabled="isReadOnly"
                  ></AppDateTimePicker>
                  <b-form-invalid-feedback
                    :state="!deepGet(v$, 'end_at.$error')"
                  >
                    輸入的資料有誤或未填寫，請確認
                  </b-form-invalid-feedback>
                </b-form-group>
                <!-- 補充時間欄位 end-->

                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center mt-4">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button
                    v-if="!isReadOnly"
                    class="mr-3"
                    @click="submit"
                    variant="success"
                  >
                    儲存
                  </b-button>
                </div>
                <!-- 表單底部 End -->
              </div>
              <!-- end: dynamic form -->
            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
    <!-- Form End -->
    <SelectStaffStatic
      ref="staffSelection"
      :staffs="availableStaffs"
      :is-loading="isFetchingStaffs"
      @selected="handleStaffSelected"
    ></SelectStaffStatic>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import bookingApi from "@/apis/booking";
import bookingPresetApi from '@/apis/booking-preset'
import AppDateTimePicker from "@/components/AppDateTimePicker";
import DynamicFormDashboard from "@/components/DynamicForm/DynamicFormDashboard";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import StaffAddCustomer from "@/pages/Dashboard/Staff/StaffAddCustomer.vue";
import _ from "lodash";
import moment from "moment";
import Vue from "vue";
import { mapGetters } from "vuex";
import SelectStaffStatic from "@/components/Dashboard/Staff/SelectStaffStatic.vue";
import collectionApi from "@/apis/collection";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BookingPresetDataModel from "@/models/BookingPreset.model";
import DynamicFormField from "@/models/DynamicFormField.model"
import DynamicFormValuePresenter from "@/presenters/DynamicFormValue.presenter"
import BookingBusinessLogic from "@/utils/booking/BookingBusinessLogic";
import ServiceDataFetcher from "@/utils/booking/ServiceDataFetcher";
import StaffDataFetcher from "@/utils/booking/StaffDataFetcher"
import { passesVisibleCondition } from "@/utils/DynamicFormUtils"
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

const bookingBusinessLogic = new BookingBusinessLogic()

const serviceDataFetcher = Vue.observable(new ServiceDataFetcher())
serviceDataFetcher.injectCollectionApi(collectionApi)

const staffDataFetcher = Vue.observable(new StaffDataFetcher())
staffDataFetcher.injectBookingPresetApi(bookingPresetApi)

export default {
  components: {
    AppSingleSelect,
    AppDateTimePicker,
    DynamicFormDashboard,
    StaffAddCustomer,
    SelectStaffStatic,
    BreadCrumb
  },
  setup: () => ({ v$: useVuelidate({ $scope: false }) }),
  validations() {
    return {
      start_at: this.hasSetUpFieldMappingForStartAt ? {} : { required },
      end_at: this.shouldShowDefaultEndAtField ? { required } : {},
    };
  },
  data() {
    return {
      consts,
      showLoading: false,
      showAddStaff: false,
      bookingPreset: {
        name: null,
        config: {
          fields: [],
        },
      },
      start_at: null,
      end_at: null,
      inputForm: {},
      booking: {
        customer_id: null,
        service_id: [],
        staff_id: null,
        branch_id: null,
        start_at: null,
        end_at: null,
        data: [],
        select_branch: {}
      },
      customer: null,
      initialized: false,
      collectionOfService: null,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    ...mapGetters("route", ["routeQuery", "routeParams"]),
    ...mapGetters("general", ["myBranches", "currentBranch"]),
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
    }),
    addCustomerButtonText() {
      return this.customer ? "重新選擇會員" : "選擇此預約所屬會員";
    },
    bookingPresetIdFromRoute() {
      return this.$route.params.id
    },
    bookingIdFromRoute() {
      return this.$route.params.booking_id
    },
    isBookingCenter() {
      return this.$route.name.includes("BookingCenter");
    },
    isEditing() {
      return ["BookingEdit", "BookingCenterBookingEdit"].includes(this.$route.name)
    },
    isReadOnly() {
      return ["BookingView", "BookingCenterBookingView"].includes(this.$route.name)
    },
    disabledBranchSelect() {
      return !this.checkPermission([this.consts.BOOKING_MANAGE_BRANCH]) &&
        (this.isEditing || this.booking.branch_id)
    },
    dataModel() {
      return new BookingPresetDataModel(this.bookingPreset)
    },
    hasSetUpFieldMappingForStartAt() {
      return this.dataModel.hasSetUpFieldMappingForStartAt
    },
    hasSetUpFieldMappingForEndAt() {
      return this.dataModel.hasSetUpFieldMappingForEndAt
    },
    canMultipleSelect() {
      return this.dataModel.canSelectMultipleServices
    },
    branchFieldId() {
      return this.dataModel.branchFieldId
    },
    branchField() {
      return this.bookingPreset.config.fields.find((field) => field._id === this.branchFieldId)
    },
    selectedBranchId() {
      return this.booking.select_branch?.value ?? null
    },
    selectedBranch() {
      return this.myBranches.find(branch => branch.id === this.selectedBranchId)
    },
    branchValueForDynamicForm() {
      return this.selectedBranchId ? DynamicFormValuePresenter.branch(this.branchField, this.selectedBranch) : null
    },
    serviceFieldId() {
      return this.dataModel.serviceFieldId
    },
    serviceField() {
      return this.bookingPreset.config.fields.find((field) => field._id === this.serviceFieldId)
    },
    collectionIdForService() {
      return this.dataModel.collectionIdForService
    },
    fieldOrder() {
      return this.dataModel.wizardFieldOrder
    },
    isLoadingServices() {
      return serviceDataFetcher.isFetching
    },
    serviceOptions() {
      return serviceDataFetcher.services.map(service => ({
        value: service.id,
        text: service.title,
      }))
    },
    branchOptions() {
      const options = [
        { value: null, text: "請選擇分店" },
        ...this.myBranches.map((branch) => ({ value: branch.id, text: `${branch.branch_code} ${branch.name}` })),
      ];

      if (!options.find((o) => o.value == this.booking.branch_id)) {
        options.push({ value: this.booking.branch_id, text: this.booking.branch.name });
      }
      return options;
    },
    subjects() {
      return DynamicFormField.fromArray(_.get(this.bookingPreset, 'config.fields', [])).map((fieldModel) => {
        return {
          ...fieldModel.flattenedAttributes(),
          disabled: this.isEditing && fieldModel._id == this.branchFieldId && fieldModel.type == 'shop_selection',
        };
      });
    },
    visibleSubjects() {
      return this.subjects.filter(subject => {
        return passesVisibleCondition(_.get(subject, "visible_when_any", []), this.subjects, this.inputForm) &&
          !(this.dataModel.isWizardMode && [this.branchFieldId, this.serviceFieldId].includes(subject._id)) // 已在 DynamicForm 之上顯示分店與服務，因此 DynamicForm 不需再次顯示
      }).map(subject => ({
        ...subject,
        disabled: subject.disabled || this.isReadOnly,
      }));
    },
    isFetchingStaffs() {
      return staffDataFetcher.isFetching
    },
    availableStaffs() {
      return staffDataFetcher.staffs
    },
    selectedServiceIdsAsArray() {
      if (!this.booking.service_id) {
        return []
      }
      return Array.isArray(this.booking.service_id) ? this.booking.service_id : [this.booking.service_id]
    },
    selectedServices() {
      return serviceDataFetcher.services.filter(service => this.selectedServiceIdsAsArray.includes(service.id))
    },
    serviceDuration() {
      return _.sumBy(this.selectedServices, 'duration')
    },
    serviceValueForDynamicForm() {
      return this.selectedServices
        .map(service => DynamicFormValuePresenter.collectionItem(this.serviceField, service._rawData, this.collectionOfService))
    },
    valueOfStartAt() {
      if (!this.hasSetUpFieldMappingForStartAt) {
        return this.start_at
      }
      return this.inputForm[this.dataModel.startAtFieldId]
    },
    shouldShowDefaultEndAtField() {
      return (this.dataModel.isWizardMode && this.isEditing) ||
        (!this.dataModel.isWizardMode && !this.hasSetUpFieldMappingForEndAt)
    },
  },
  methods: {
    deepGet: _.get,
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async init() {
      const initializeDataForForm = async () => {
        if (this.isEditing || this.isReadOnly) {
          await this.fetchBooking();
          this.booking.select_branch = this.branchOptions.find(branch => branch.value === this.booking.branch_id);
        } else {
          this.booking.branch_id = this.$route.query.branch_id || this.currentBranch?.id
          if (this.booking.branch_id) {
            this.booking.select_branch = this.branchOptions.find(branch => branch.value === this.booking.branch_id)
            this.$set(this.inputForm, this.branchFieldId, this.branchValueForDynamicForm)
          }
        }

        await Promise.all([
          initializeServices(),
          initializeStaffs(),
        ])
      }

      const initializeServices = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        if (bookingBusinessLogic.servicesAreBoundToBranches() && this.booking.branch_id) {
          await serviceDataFetcher.fetchServicesByBranch(this.collectionIdForService, this.booking.branch_id)
        } else if (bookingBusinessLogic.servicesAreBoundToStaffs() && this.booking.staff_id) {
          await serviceDataFetcher.fetchServicesByStaff(this.collectionIdForService, this.booking.staff_id)
        } else {
          await serviceDataFetcher.fetchServices(this.collectionIdForService)
        }

        if (this.selectedServiceIdsAsArray.length > 0) {
          this.$set(this.inputForm, this.serviceFieldId, this.serviceValueForDynamicForm)
        }
      }

      const initializeStaffs = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        if (bookingBusinessLogic.staffsAreBoundToBranches() && this.booking.branch_id) {
          await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
        } else if (bookingBusinessLogic.staffsAreBoundToServices() && this.selectedServiceIdsAsArray.length > 0) {
          await staffDataFetcher.fetchAvailableStaffs(this.selectedServiceIdsAsArray, this.booking.branch_id, this.bookingPresetIdFromRoute)
        } else if (this.booking.branch_id) {
          await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
        }
      }

      const initializeCollectionOfService = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        this.collectionOfService = _.get(await collectionApi.getCollection(this.collectionIdForService), 'data.data')
      }

      this.showLoading = true;
      await this.fetchBookingPreset();
      await Promise.all([
        initializeDataForForm(),
        initializeCollectionOfService(),
        this.$refs.dynamicForm.fetchAllAsyncOptions(),
      ])
      this.initialized = true;
      this.showLoading = false;
    },
    cancel() {
      this.$router.go(-1);
    },
    async submit() {
      // 先驗證 vuelidate 的字段
      const isVuelidateValid = await this.v$.$validate();

      // 如果 vuelidate 驗證不通過，退出函數並顯示錯誤
      if (!isVuelidateValid) {
        const element = document.getElementById(this.v$.$errors[0].$property);
        element.scrollIntoView({ behavior: "smooth" });
        console.warn("[BookingForm] Validation failed.");
        return;
      }

      const validate = await this.$refs.dynamicForm.validate();
      if (!validate) return;

      try {
        this.showLoading = true;

        const bookingDataToSave = {
          fields: DynamicFormField.fromArray(this.bookingPreset.config.fields).reduce((acc, fieldModel) => {
            return { ...acc, [fieldModel._id]: this.inputForm[fieldModel._id] ?? null };
          }, {}),
          customer_id: this.customer ? this.customer.id : null,
        };

        if (false === this.hasSetUpFieldMappingForStartAt) {
          bookingDataToSave.start_at = this.start_at;
        }

        if (this.shouldShowDefaultEndAtField) {
          bookingDataToSave.end_at = this.end_at;
        }

        if (this.dataModel.isWizardMode && !this.isEditing) {
          bookingDataToSave.end_at = bookingBusinessLogic.endDateTimeToSubmitInWizard(this.valueOfStartAt, this.serviceDuration).format('YYYY-MM-DD HH:mm:ss')
        }

        if (this.dataModel.isWizardMode) {
          bookingDataToSave.staff_id = this.booking.staff_id;
          bookingDataToSave.branch_id = this.booking.branch_id;
          if (this.canMultipleSelect) {
            bookingDataToSave.service_id = this.booking.service_id ?? [];
          } else {
            bookingDataToSave.service_id = this.booking.service_id ? [this.booking.service_id] : [];
          }
        }

        let title = '';
        // 編輯表單
        if (this.isEditing) {
          await bookingApi.updateBooking(
            this.bookingPresetIdFromRoute,
            this.bookingIdFromRoute,
            bookingDataToSave
          );
          title = "儲存成功";
        }
        // 新增表單
        else {
          await bookingApi.createBooking(
            this.bookingPresetIdFromRoute,
            bookingDataToSave
          );
          title = "新增成功";
        }

        const result = await this.$swal({
          title,
          type: "success",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "確認",
          confirmButtonColor: "#41b882",
          cancelButtonText: "回上一頁",
          cancelButtonClass: "cancel-button",
          allowOutsideClick: false,  // 禁止點擊視窗外的區域
        });

        if (!result.value) {
          this.$router.go(-1);
        }

        return true;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
        return false;
      } finally {
        this.showLoading = false;
      }
    },
    async fetchBookingPreset() {
      if (!this.bookingPresetIdFromRoute) {
        this.$swal("錯誤", "無法取得讀取 ID", "error");
        return;
      }

      try {
        this.bookingPreset = _.get(await bookingApi.getBookingPreset(this.bookingPresetIdFromRoute), 'data.data', {});
        bookingBusinessLogic.setBookingPresetDataModel(this.dataModel)
        serviceDataFetcher.setServiceFieldMappings(this.dataModel.serviceFieldMappings)

        if (_.get(this.bookingPreset, 'config.fields', []).length === 0) {
          this.$swal("提醒", "此預約尚未設定欄位，須設定欄位後才能新增及編輯預約", "warning");
          this.cancel();
          return;
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料錯誤", "error");
        this.cancel();
      }
    },
    async fetchBooking() {
      try {
        const response = await bookingApi.getBooking(
          this.bookingPresetIdFromRoute,
          this.bookingIdFromRoute
        );
        this.booking = response.data;

        this.start_at = this.booking.start_at;
        this.end_at = moment(this.booking.end_at).add(1, "second").format("YYYY-MM-DD HH:mm:ss");
        this.customer = this.booking.customer;

        if (this.booking.service_id) {
          if (this.canMultipleSelect) {
            this.booking.service_id = JSON.parse(this.booking.service_id);
          } else {
            try {
              this.booking.service_id =
                JSON.parse(this.booking.service_id)[0] ?? null;
            } catch (e) {
              // console.error(e);
            }
          }
        } else {
          this.booking.service_id = [];
        }

        this.inputForm = DynamicFormField.fromArray(this.bookingPreset.config.fields).reduce((acc, fieldModel) => {
          const fieldData = this.booking.data.find((data) => data.field_id === fieldModel._id)?.value ?? null;
          return { ...acc, [fieldModel._id]: fieldModel.prepareFieldValueForTwoWayBinding(fieldData) };
        }, {});
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取錯誤", "error");
      }
    },
    async handleStaffSelected(staff) {
      this.booking.staff_id = staff.id;
      this.booking.staff = staff;

      if (bookingBusinessLogic.servicesAreBoundToStaffs()) {
        this.booking.service_id = this.canMultipleSelect ? [] : null
        await serviceDataFetcher.fetchServicesByStaff(this.collectionIdForService, this.booking.staff_id)
      }
    },
    async handleBranchChange() {
      this.booking.branch_id = this.selectedBranchId
      this.$set(this.inputForm, this.branchFieldId, this.branchValueForDynamicForm)

      if (bookingBusinessLogic.servicesAreBoundToBranches()) {
        this.booking.service_id = this.canMultipleSelect ? [] : null
        await serviceDataFetcher.fetchServicesByBranch(this.collectionIdForService, this.booking.branch_id)
      }

      if (bookingBusinessLogic.staffsAreBoundToBranches()) {
        await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
      }
    },
    async handleServiceSelected() {
      this.$set(this.inputForm, this.serviceFieldId, this.serviceValueForDynamicForm)

      if (bookingBusinessLogic.staffsAreBoundToServices()) {
        await staffDataFetcher.fetchAvailableStaffs(this.selectedServiceIdsAsArray, this.booking.branch_id, this.bookingPresetIdFromRoute)
      }
    },

    async handleSelectCustomer(customer) {
      this.$swal({
        title: "確定要加入會員嗎",
        html: `
          <div class="d-block">
            <div class="my-3 text-left">
              <div>會員姓名:${customer.name}</div>
            </div>
          </div>`,
        type: "warning",
        showConfirmButton: true,
        showCancelButton: true,
        cancelButtonText: "返回",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "確認加入",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.value) {
          try {
            this.customer = customer;
          } catch (e) {
            console.error(e);
          } finally {
            this.$refs.staffAddCustomer.refreshSearch();
            this.$refs.staffAddCustomer.closeModal();
          }
        }
      });
    },
    displayCustomerInfo(customer) {
      return (
        (customer.outer_code ? customer.outer_code + " " : "") +
        (customer.mobile_phone ?? "")
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-items > li {
  cursor: pointer;

  &.disabled {
    background-color: #ddd;
    opacity: 0.5;
    pointer-events: none;
  }

  .icon-for-done {
    display: none;
  }

  &.done {
    i {
      color: green;
    }

    .icon-for-done {
      display: unset;
    }

    .icon-for-undone {
      display: none;
    }
  }
}
</style>

<style>
.cancel-button {
  background-color: #ffffff !important;
  color: #41b882 !important; /* 文字顏色，確保可讀性 */
  border: 2px solid #41b882 !important; /* 正確設定邊框 */
}
.sorted-fields {
  display: flex;
  flex-direction: column;
}
</style>
